import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, FormControlLabel, Checkbox, Autocomplete,
    useTheme,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { tokens } from '../../../theme';
import v2Api from '../../../utils/v2Api';

export default function UpdateModalV2({
    open, onClose, courseTest, students, studentsStartEndDateMap,
    userRole, setBackdropOpen, setSuccessAlertOpen, setErrorAlertOpen,
    setSuccessAlertMessage, setErrorAlertMessage,
}) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [dueDate, setDueDate] = useState(null);
    const [showOnHomework, setShowOnHomework] = useState(true);
    const [selectedStudents, setSelectedStudents] = useState([]);

    useEffect(() => {
        if (courseTest && open) {
            setTitle(courseTest.title || '');
            setContent(courseTest.content || '');
            setDueDate(courseTest.due_date ? moment(courseTest.due_date) : null);
            setShowOnHomework(courseTest.show_on_homework ?? true);

            // Pre-select assigned students
            const assignedIds = (courseTest.v2_course_test_students || []).map(s => s.student_id);
            const matched = (students || []).filter(s => assignedIds.includes(s.id));
            setSelectedStudents(matched);
        }
    }, [courseTest, open, students]);

    const handleSave = async () => {
        setBackdropOpen(true);
        try {
            const studentIds = selectedStudents.length > 0
                ? selectedStudents.map(s => s.id)
                : [];

            await v2Api.put(`/course-tests/${courseTest.id}`, {
                title, content, due_date: dueDate,
                show_on_homework: showOnHomework,
                student_ids: studentIds,
            });

            setBackdropOpen(false);
            setSuccessAlertMessage('Test updated successfully!');
            setSuccessAlertOpen(true);
            onClose();
            window.location.reload();
        } catch (err) {
            setBackdropOpen(false);
            setErrorAlertMessage(err.response?.data?.message || 'Failed to update');
            setErrorAlertOpen(true);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit V2 Test Assignment</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
                <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth />
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateTimePicker label="Due Date" value={dueDate} onChange={setDueDate} />
                </LocalizationProvider>
                {userRole === 'admin' && (
                    <Autocomplete
                        multiple
                        limitTags={4}
                        options={students || []}
                        getOptionLabel={(s) => `${s.first_name} ${s.last_name}`}
                        getOptionKey={(s) => s.id}
                        value={selectedStudents}
                        filterSelectedOptions
                        renderInput={(params) => <TextField {...params} label="Assigned students (empty = all)" />}
                        onChange={(_, val) => setSelectedStudents(val)}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                    />
                )}
                <TextField
                    label="Instructions"
                    multiline rows={4}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                />
                <FormControlLabel
                    control={<Checkbox checked={showOnHomework} onChange={(e) => setShowOnHomework(e.target.checked)} />}
                    label="Show on homework page"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}
